import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '../services/authApi';

export interface User {
  id: string;
  email: string;
  name?: string;
  is_platform_admin?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requestOTP: (email: string) => Promise<void>;
  verifyOTP: (email: string, otp: string) => Promise<void>;
  googleLogin: (code: string, redirectUri: string) => Promise<void>;
  setToken: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true, // true at boot to avoid a flash-redirect to /login

      requestOTP: async (email: string) => {
        await authService.requestOTP(email);
      },

      verifyOTP: async (email: string, otp: string) => {
        const response = await authService.verifyOTP(email, otp);
        const { token, user } = response.data;
        localStorage.setItem('training_token', token);
        set({ user, token, isAuthenticated: true, isLoading: false });
      },

      googleLogin: async (code: string, redirectUri: string) => {
        const response = await authService.googleLogin(code, redirectUri);
        const { token, user } = response.data;
        localStorage.setItem('training_token', token);
        set({ user, token, isAuthenticated: true, isLoading: false });
      },

      // SSO: accept a JWT handed over from the customer portal, then validate
      // it by fetching the profile before treating the session as real.
      setToken: async (token: string) => {
        localStorage.setItem('training_token', token);
        set({ token, isAuthenticated: true, isLoading: true });
        try {
          const profile = await authService.getProfile();
          set({ user: profile.data, isAuthenticated: true, isLoading: false });
        } catch {
          localStorage.removeItem('training_token');
          set({ user: null, token: null, isAuthenticated: false, isLoading: false });
          throw new Error('token validation failed');
        }
      },

      logout: async () => {
        // Client-side only. We deliberately do NOT call the server logout: it
        // blacklists the shared SSO JWT, which would break every other product
        // (customer portal, inference, …) that authenticates with the same
        // token — and, once re-handed-back over SSO, causes an auth reload loop.
        localStorage.removeItem('training_token');
        set({ user: null, token: null, isAuthenticated: false, isLoading: false });
      },

      checkAuth: async () => {
        const token = localStorage.getItem('training_token');
        if (!token) {
          set({ isAuthenticated: false, isLoading: false });
          return;
        }
        try {
          const profile = await authService.getProfile();
          set({ user: profile.data, token, isAuthenticated: true, isLoading: false });
        } catch {
          localStorage.removeItem('training_token');
          set({ user: null, token: null, isAuthenticated: false, isLoading: false });
        }
      },
    }),
    { name: 'training-auth-storage', partialize: (s) => ({ user: s.user, token: s.token }) }
  )
);
